import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import { X } from "phosphor-react";
import EditDetailsTab from "./EditDetailsTab";
import EditCustomPageColumnsTab from "./EditCustomPageColumnsTab";
import CustomPageConditionsEdit from "../CustomPageConditionsEdit";
import { fpatch } from "../../../API/callsAPI";

export default function EditCustomPageModal({
  handleClose,
  projectId,
  pageData,
  onSuccess,
}) {
  const [activeTab, setActiveTab] = useState("details");
  const [isLoading, setIsLoading] = useState(false);

  // Store original data for change detection
  const originalData = useMemo(
    () => ({
      name: pageData?.name || "",
      description: pageData?.description || "",
      page_config: pageData?.page_config || {},
      filter: pageData?.filter || [],
    }),
    [pageData]
  );

  const [formData, setFormData] = useState({
    pageName: originalData.name,
    description: originalData.description,
  });

  const trackable = {
    id: pageData?.trackable?.id || pageData?.trackable || null,
    name: pageData?.trackable?.name || null,
  };

  // Convert page_config to initial columns array
  const convertPageConfigToColumns = (pageConfig) => {
    if (!pageConfig || Object.keys(pageConfig).length === 0) {
      return [];
    }
    return Object.entries(pageConfig)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([_, name]) => ({ name }));
  };

  const [selectedColumns, setSelectedColumns] = useState(
    convertPageConfigToColumns(originalData.page_config)
  );
  const [selectedPageFilter, setSelectedPageFilter] = useState(
    originalData.filter
  );

  // Convert API filter format to component internal format
  const convertApiFiltersToInternal = (apiFilters) => {
    if (!apiFilters || !Array.isArray(apiFilters) || apiFilters.length === 0) {
      return [];
    }

    const reverseOperatorMap = {
      "=": "equals",
      "!=": "not_equals",
      contains: "contains",
      not_contains: "not_contains",
      "<": "less_than",
      ">": "greater_than",
      is_empty: "is_empty",
      is_not_empty: "is_not_empty",
    };

    return apiFilters.map((filter, index) => ({
      id: Date.now() + index,
      field: filter[0] || "",
      operator: reverseOperatorMap[filter[1]] || filter[1] || "",
      value: filter[2] || "",
    }));
  };

  const initialFilters = convertApiFiltersToInternal(pageData?.filter);

  const tabs = [
    { label: "Details", value: "details" },
    { label: "Columns", value: "columns" },
    { label: "Conditions", value: "conditions" },
    { label: "Access", value: "access" },
  ];

  // Detect if any changes have been made
  const hasChanges = useMemo(() => {
    // Check name and description changes
    if (
      formData.pageName !== originalData.name ||
      formData.description !== originalData.description
    ) {
      return true;
    }

    // Check columns changes (compare page_config)
    const currentPageConfig = {};
    selectedColumns.forEach((col, index) => {
      currentPageConfig[index] = col.name || col.identifier;
    });

    const originalPageConfigKeys = Object.keys(originalData.page_config);
    const currentPageConfigKeys = Object.keys(currentPageConfig);

    if (originalPageConfigKeys.length !== currentPageConfigKeys.length) {
      return true;
    }

    for (let key of originalPageConfigKeys) {
      if (originalData.page_config[key] !== currentPageConfig[key]) {
        return true;
      }
    }

    // Check filter changes
    if (JSON.stringify(selectedPageFilter) !== JSON.stringify(originalData.filter)) {
      return true;
    }

    return false;
  }, [formData, selectedColumns, selectedPageFilter, originalData]);

  const handleSave = async () => {
    setIsLoading(true);

    try {
      // Convert selected columns to page_config format
      const pageConfig = {};
      selectedColumns.forEach((column, index) => {
        pageConfig[index] = column.name || column.identifier;
      });

      const updatePayload = {
        name: formData.pageName.trim(),
        description: formData.description.trim(),
        filter: selectedPageFilter,
        page_config: pageConfig,
      };

      const res = await fpatch({
        url: `trackables/custom-page/${pageData.id}/`,
        data: updatePayload,
      });

      if (res.status === 200 || res.status === 201) {
        if (onSuccess) onSuccess();
        handleClose();
      } else {
        console.error("Failed to update page:", res);
      }
    } catch (error) {
      console.error("Error updating page:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "details":
        return (
          <EditDetailsTab
            formData={formData}
            setFormData={setFormData}
            trackableName={trackable.name}
          />
        );
      case "columns":
        return (
          <EditCustomPageColumnsTab
            projectId={projectId}
            trackableId={trackable.id}
            trackableName={trackable.name}
            initialColumns={pageData?.page_config || {}}
            onSelectColumns={(columns) => setSelectedColumns(columns)}
          />
        );
      case "conditions":
        return (
          <CustomPageConditionsEdit
            projectId={projectId}
            selectedTrackable={trackable}
            trackType={trackable.name}
            trackableId={trackable.id}
            initialFilter={initialFilters}
            onFilterChange={(filterData) => setSelectedPageFilter(filterData)}
          />
        );
      case "access":
        return (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ color: "#606479" }}>
              Access control coming soon...
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "100%" },
        maxWidth: "100vw",
        height: "100%",
        bgcolor: "#1B1D28",
        color: "#F9F9F9",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2.5,
          borderBottom: "1px solid #606479",
        }}
      >
        <Typography sx={{ fontSize: "20px !important", fontWeight: 500 }}>
          Edit Page
        </Typography>
        <Button
          sx={{ padding: 0, minWidth: 0, color: "white" }}
          onClick={handleClose}
        >
          <X size={20} />
        </Button>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, value) => setActiveTab(value)}
        sx={{
          px: 2.5,
          borderBottom: "1px solid #606479",
          "& .MuiTab-root": {
            color: "#606479",
            textTransform: "capitalize",
            fontSize: "14px",
            fontWeight: 400,
            minHeight: "48px",
            "&.Mui-selected": {
              color: "white",
              fontWeight: 500,
            },
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "#6C65D9",
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2.5,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#606479",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: "#707489",
            },
          },
        }}
      >
        {renderTabContent()}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          p: 2.5,
          borderTop: "1px solid #606479",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          sx={{
            borderColor: "#606479",
            color: "white",
            borderRadius: "10px",
            textTransform: "none",
            px: 4,
            py: 1,
            "&:hover": {
              borderColor: "#707489",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
            },
            "&.Mui-disabled": {
              borderColor: "#606479",
              color: "rgba(255, 255, 255, 0.3)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            formData.pageName.trim() === "" || !hasChanges || isLoading
          }
          sx={{
            backgroundColor: "#6C65D9",
            borderRadius: "10px",
            textTransform: "none",
            px: 4,
            py: 1,
            "&:hover": {
              backgroundColor: "#5A55C7",
            },
            "&.Mui-disabled": {
              backgroundColor: "#606479",
              color: "rgba(255, 255, 255, 0.5)",
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: "white" }} />
          ) : (
            "Apply Changes"
          )}
        </Button>
      </Box>
    </Box>
  );
}
